// ---------------------------------------------------------------------------
// useProgressiveLoading -- Hook for Managing Progressive Loading Steps
// ---------------------------------------------------------------------------
// Manages loading state with multiple steps, auto-advancing through stages.
// ---------------------------------------------------------------------------

import { useState, useCallback, useEffect, useRef } from 'react'
import type { LoadingStep, StepStatus } from '../components/ChartViewLoader'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseProgressiveLoadingOptions {
  readonly steps: readonly Omit<LoadingStep, 'status'>[]
  readonly onComplete?: () => void
}

export interface UseProgressiveLoadingReturn {
  readonly steps: readonly LoadingStep[]
  readonly currentStepIndex: number
  readonly isLoading: boolean
  readonly progress: number
  readonly startLoading: () => void
  readonly completeCurrentStep: (count?: number, duration?: number) => void
  readonly reset: () => void
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useProgressiveLoading({
  steps: initialSteps,
  onComplete,
}: UseProgressiveLoadingOptions): UseProgressiveLoadingReturn {
  const [steps, setSteps] = useState<LoadingStep[]>(
    initialSteps.map((step) => ({ ...step, status: 'pending' as StepStatus }))
  )
  const [currentStepIndex, setCurrentStepIndex] = useState(-1)
  const isLoadingRef = useRef(false)

  const isLoading = currentStepIndex >= 0 && currentStepIndex < steps.length

  // Calculate progress (0-100)
  const progress =
    steps.length === 0
      ? 100
      : (steps.filter((s) => s.status === 'done').length / steps.length) * 100

  /**
   * Start loading sequence
   */
  const startLoading = useCallback(() => {
    if (isLoadingRef.current) return

    isLoadingRef.current = true
    setCurrentStepIndex(0)

    // Set first step to loading
    setSteps((prev) =>
      prev.map((step, index) => ({
        ...step,
        status: index === 0 ? 'loading' : 'pending',
      }))
    )
  }, [])

  /**
   * Mark current step as complete and advance to next
   */
  const completeCurrentStep = useCallback(
    (count?: number, duration?: number) => {
      setSteps((prev) =>
        prev.map((step, index) => {
          if (index === currentStepIndex) {
            return { ...step, status: 'done', count, duration }
          }
          if (index === currentStepIndex + 1) {
            return { ...step, status: 'loading' }
          }
          return step
        })
      )

      setCurrentStepIndex((prev) => prev + 1)
    },
    [currentStepIndex]
  )

  /**
   * Reset to initial state
   */
  const reset = useCallback(() => {
    isLoadingRef.current = false
    setCurrentStepIndex(-1)
    setSteps(
      initialSteps.map((step) => ({ ...step, status: 'pending' as StepStatus }))
    )
  }, [initialSteps])

  /**
   * Auto-call onComplete when all steps are done
   */
  useEffect(() => {
    if (currentStepIndex >= steps.length && isLoadingRef.current) {
      isLoadingRef.current = false
      onComplete?.()
    }
  }, [currentStepIndex, steps.length, onComplete])

  return {
    steps,
    currentStepIndex,
    isLoading,
    progress,
    startLoading,
    completeCurrentStep,
    reset,
  }
}
